import React, { createContext, useContext, useState, useEffect } from 'react';
import { theme as antdTheme } from 'antd';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or default to 'light'
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('pathmarkr-theme');
    return savedTheme || 'light';
  });

  // Save theme to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('pathmarkr-theme', theme);
    
    // Update document class for global CSS theming
    document.documentElement.setAttribute('data-theme', theme);
    
    // Update body class for compatibility
    document.body.className = theme === 'dark' ? 'dark-theme' : 'light-theme';
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const isDark = theme === 'dark';

  // Ant Design theme tokens for light mode
  const lightTheme = {
    token: {
      colorPrimary: '#1890ff',
      colorBgBase: '#ffffff',
      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorBgLayout: '#f0f2f5',
      colorBgSpotlight: '#ffffff',
      colorBorder: '#d9d9d9',
      colorBorderSecondary: '#f0f0f0',
      colorFill: '#f5f5f5',
      colorFillSecondary: '#fafafa',
      colorFillTertiary: '#f5f5f5',
      colorFillQuaternary: '#f0f0f0',
      colorText: '#000000d9',
      colorTextSecondary: '#00000073',
      colorTextTertiary: '#00000040',
      colorTextQuaternary: '#00000026',
      borderRadius: 8,
      colorLink: '#1890ff',
      colorLinkHover: '#40a9ff',
      colorLinkActive: '#096dd9',
    },
    components: {
      Layout: {
        headerBg: '#ffffff',
        headerHeight: 64,
        headerPadding: '0 24px',
        headerColor: 'rgba(0, 0, 0, 0.88)',
        siderBg: '#001529',
        triggerBg: '#002140',
        triggerColor: '#fff',
        bodyBg: '#f0f2f5',
      },
      Menu: {
        darkItemBg: '#001529',
        darkItemColor: 'rgba(255, 255, 255, 0.65)',
        darkItemSelectedBg: '#1890ff',
        darkItemSelectedColor: '#fff',
        darkItemHoverBg: '#1890ff33',
        darkItemHoverColor: '#fff',
        darkSubMenuItemBg: '#000c17',
      },
      Card: {
        colorBgContainer: '#ffffff',
        colorBorderSecondary: '#f0f0f0',
      },
      Table: {
        colorBgContainer: '#ffffff',
        headerBg: '#fafafa',
      },
    },
  };

  // Ant Design theme tokens for dark mode
  const darkTheme = {
    token: {
      colorPrimary: '#1890ff',
      colorBgBase: '#141414',
      colorBgContainer: '#1f1f1f',
      colorBgElevated: '#262626',
      colorBgLayout: '#000000',
      colorBgSpotlight: '#262626',
      colorBorder: '#434343',
      colorBorderSecondary: '#303030',
      colorFill: '#262626',
      colorFillSecondary: '#1f1f1f',
      colorFillTertiary: '#262626',
      colorFillQuaternary: '#303030',
      colorText: '#ffffffd9',
      colorTextSecondary: '#ffffff73',
      colorTextTertiary: '#ffffff40',
      colorTextQuaternary: '#ffffff26',
      borderRadius: 8,
      colorLink: '#1890ff',
      colorLinkHover: '#40a9ff',
      colorLinkActive: '#096dd9',
    },
    components: {
      Layout: {
        headerBg: '#1f1f1f',
        headerHeight: 64,
        headerPadding: '0 24px',
        headerColor: 'rgba(255, 255, 255, 0.88)',
        siderBg: '#001529',
        triggerBg: '#002140',
        triggerColor: '#fff',
        bodyBg: '#141414',
      },
      Menu: {
        darkItemBg: '#001529',
        darkItemColor: 'rgba(255, 255, 255, 0.65)',
        darkItemSelectedBg: '#1890ff',
        darkItemSelectedColor: '#fff',
        darkItemHoverBg: '#1890ff33',
        darkItemHoverColor: '#fff',
        darkSubMenuItemBg: '#000c17',
      },
      Card: {
        colorBgContainer: '#1f1f1f',
        colorBorderSecondary: '#303030',
      },
      Table: {
        colorBgContainer: '#1f1f1f',
        headerBg: '#262626',
      },
      Input: {
        colorBgContainer: '#1f1f1f',
        colorBorder: '#434343',
      },
      Select: {
        colorBgContainer: '#1f1f1f',
        colorBorder: '#434343',
      },
      Button: {
        colorBgContainer: '#1f1f1f',
        colorBorder: '#434343',
      },
      Modal: {
        contentBg: '#1f1f1f',
        headerBg: '#1f1f1f',
      },
      Drawer: {
        colorBgElevated: '#1f1f1f',
      },
      Dropdown: {
        colorBgElevated: '#1f1f1f',
      },
      Popover: {
        colorBgElevated: '#1f1f1f',
      },
      Tooltip: {
        colorBgSpotlight: '#262626',
      },
    },
    algorithm: antdTheme.darkAlgorithm,
  };

  const currentTheme = isDark ? darkTheme : lightTheme;

  const value = {
    theme,
    isDark,
    toggleTheme,
    antdTheme: currentTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
